'use client';
import React, { useCallback, useEffect, useState } from 'react';
import ProfileContext, {
  ProfileContextProps,
} from '@src/v3/contexts/userProfile';
import { getUserProfile } from '@src/v3/services/auth';
import { transformRenterProfile } from '@src/v3/utils/helper';
import { getItem, isLoggedIn } from '@src/v3/utils/localStorage';
import { ZEME_USER_DATA } from '@src/v3/constants/localStorage';
import { RenterProfile } from '@src/v3/interfaces/profile';
import { useRouter, usePathname } from 'next/navigation';
import { APP_ROUTE } from '@src/v3/constants/routes';

interface ProfileProviderProps {
  children: React.ReactNode;
}

const PUBLIC_ROUTES = [
  APP_ROUTE.PRIVACY,
  APP_ROUTE.TERMS,
  APP_ROUTE.TU_RENTER_TERMS,
  APP_ROUTE.TU_AGENT_TERMS,
];

const ProfileProvider: React.FC<ProfileProviderProps> = ({ children }) => {
  const { push } = useRouter();
  const pathname = usePathname();
  const [profile, setUserProfile] = useState<RenterProfile | null>(null);
  const [isUserLoggedIn, setIsUserLoggedIn] = useState<boolean>(isLoggedIn());

  /**
   * This method is used to fetch user profile from server.
   */
  const fetchMyProfile = useCallback(
    // eslint-disable-next-line no-unused-vars
    async (callback?: (profile: RenterProfile | null) => void) => {
      try {
        const {
          user_id,
          account_type,
        }: { user_id: string; account_type: string } = getItem(
          ZEME_USER_DATA
        ) || {
          user_id: '',
          account_type: '',
        };

        const res = await getUserProfile(
          account_type.toLowerCase(),
          String(user_id || '')
        );
        const profile = transformRenterProfile(res);
        setProfile(profile);

        if (callback) callback(profile);
      } catch (error) {
        console.error(error);
      }
    },
    []
  );

  /**
   * Call on component mount
   */
  useEffect(() => {
    if (isUserLoggedIn && pathname && !PUBLIC_ROUTES.includes(pathname)) {
      fetchMyProfile((profile) => {
        if (!profile?.generalInformation?.verifiedSms) {
          push(APP_ROUTE.VERIFY_CODE);
          return;
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchMyProfile, isUserLoggedIn, pathname]);

  /**
   * This is method is used to set profile context.
   * @param {object} profile
   */
  const setProfile = (profile: RenterProfile | null): void => {
    setUserProfile(profile);
  };

  const profileContextValue: ProfileContextProps = {
    profile,
    setProfile,
    fetchMyProfile,
    setIsUserLoggedIn,
    isUserLoggedIn,
  };

  return (
    <ProfileContext.Provider value={profileContextValue}>
      {children}
    </ProfileContext.Provider>
  );
};

export default ProfileProvider;
